import React, { useCallback, useLayoutEffect, useRef } from 'react';

type AllowedTag = 'span' | 'div' | 'strong' | 'b' | 'p';

interface TamperResistantTextProps {
  children: string | number | null | undefined;
  as?: AllowedTag;
  style?: React.CSSProperties;
  className?: string;
  /** Rendered as data-testid so e2e specs can anchor on the guarded text. */
  testId?: string;
}

/**
 * Patient-safety guard for text that must not be rewritten by browser extensions
 * or built-in translators (e.g. Grammarly, Edge Translate, in-page rewriters).
 *
 * Layer 1 (soft): translate="no", spellcheck="false", data-gramm="false" — tells
 *   well-behaved extensions/translators to skip this node.
 * Layer 2 (hard): a MutationObserver detects any externally caused mutation to
 *   this node's text and immediately resets it to the React-supplied source value.
 *
 * Use this for any rendered drug name, DIN, strength, lot, expiry, doctor name,
 * or any other safety-critical content where the displayed string MUST equal the
 * value our backend sent.
 */
export const TamperResistantText: React.FC<TamperResistantTextProps> = ({
  children,
  as = 'span',
  style,
  className,
  testId,
}) => {
  const sourceText = children == null ? '' : String(children);

  const sourceTextRef = useRef(sourceText);
  const nodeRef = useRef<HTMLElement | null>(null);
  const observerRef = useRef<MutationObserver | null>(null);

  // Callback ref: re-binds the observer whenever React mounts a new host
  // element. Handles `as` changes (e.g. "span" -> "div") because React calls
  // this with the new node after mount and with `null` before unmount.
  const setNode = useCallback((node: HTMLElement | null) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    nodeRef.current = node;
    if (!node) return;
    if (node.textContent !== sourceTextRef.current) {
      node.textContent = sourceTextRef.current;
    }
    const observer = new MutationObserver(() => {
      const current = nodeRef.current;
      if (!current) return;
      if (current.textContent !== sourceTextRef.current) {
        current.textContent = sourceTextRef.current;
      }
    });
    observer.observe(node, {
      characterData: true,
      childList: true,
      subtree: true,
    });
    observerRef.current = observer;
  }, []);

  // Sync the source-of-truth ref before the MutationObserver callback can
  // fire. useLayoutEffect runs synchronously after React commits the DOM
  // update but before microtasks execute, so React's own legitimate text
  // change is not misclassified as tampering and reverted to the prior value.
  useLayoutEffect(() => {
    sourceTextRef.current = sourceText;
    const node = nodeRef.current;
    if (node && node.textContent !== sourceText) {
      node.textContent = sourceText;
    }
  }, [sourceText]);

  const Tag = as as React.ElementType;
  return (
    <Tag
      ref={setNode}
      translate="no"
      lang="en"
      spellCheck={false}
      data-gramm="false"
      data-gramm_editor="false"
      data-enable-grammarly="false"
      data-testid={testId}
      className={className}
      style={style}
    >
      {sourceText}
    </Tag>
  );
};

export default TamperResistantText;
